import type { ReactNode } from 'react';

import { AccountListRow } from '@/components/account/AccountListRow';
import { AccountSwitchRow as SwitchRow } from '@/components/account/AccountSwitchRow';
import { SharedAccountTextsRow } from '@/components/account/SharedAccountTextsRow';
import { Heading } from '@/components/ui/heading';
import { VStack } from '@/components/ui/vstack';
import { useAnnouncementBannerSetting } from '@/hooks/useAnnouncementBannerSetting';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import {
  useInboxQuestionGroupCardsOptIn,
  useInboxQuestionGroupCardsOptInStore,
} from '@/services/stores/inboxQuestionGroupCardsOptInStore';

/**
 * The account page's staff section, "Primum": the tools that act on the platform rather than on
 * the account, kept apart from Settings so a staff member finds them in one place. Developer
 * settings used to open only from a triple tap on the version number, which nobody found.
 *
 * The inquiry groups and announcement banner switches show only for the Primum group, and the inbox checks the group again
 * before it shows a nested card. Act as comes in through `actAs` and shows whenever the account
 * switcher allows it, which is decided by its own hook rather than here. The section renders
 * nothing when it would hold neither.
 *
 * A texts switch shows under Act as for each account passed in `textAccounts`, since covering a
 * shared inbox and acting as it go together. The account screen passes only the one the server
 * names for texts, Ask Anonymously.
 */
export function PrimumSection({
  actAs,
  textAccounts = [],
  onOpenDeveloperSettings,
}: {
  actAs?: ReactNode;
  /** Support accounts whose texts the signed-in staff member may switch on for themselves. */
  textAccounts?: { id: string; label: string }[];
  /** Opens Developer settings. Passed only for the accounts allowed to see them. */
  onOpenDeveloperSettings?: () => void;
}) {
  const { isPrimumUser } = useCognitoGroups();

  const optedIn = useInboxQuestionGroupCardsOptIn();

  const setOptedIn = useInboxQuestionGroupCardsOptInStore((state) => state.setOptedIn);

  const announcementBanner = useAnnouncementBannerSetting();

  if (!isPrimumUser && !actAs && !onOpenDeveloperSettings) {
    return null;
  }

  return (
    <VStack className="mb-8 w-full">
      <Heading size="h3-lg" className="mb-4 text-primary-500">
        Primum
      </Heading>

      {/* The same gap the Settings list puts between its rows, so the two sections match. */}
      <VStack className="w-full gap-4">
        {actAs}

        {textAccounts.map((account) => (
          <SharedAccountTextsRow
            key={account.id}
            accountUserId={Number(account.id)}
            accountLabel={account.label}
          />
        ))}

        {isPrimumUser ? (
          <SwitchRow
            title="Inquiry groups (beta)"
            subtitle="Group inquiries by question on this device"
            value={optedIn}
            onValueChange={setOptedIn}
          />
        ) : null}

        {isPrimumUser ? (
          <SwitchRow
            title="Announcement banner"
            subtitle="Show announcements, applied on reload"
            value={announcementBanner.enabled === true}
            disabled={announcementBanner.enabled === null}
            onValueChange={announcementBanner.setEnabled}
          />
        ) : null}

        {onOpenDeveloperSettings ? (
          <AccountListRow title="Developer settings" onPress={onOpenDeveloperSettings} />
        ) : null}
      </VStack>
    </VStack>
  );
}
